import React from 'react';

import type {BusinessPaymentMethodInfo} from '@afriex/core/domain/types';

import {Input, Select} from '@/components';
import {readSettlementAccountField} from '@/types';

import type {SettlementChannelField} from './settlementChannelForms';
import type {SettlementAccountFieldError} from './types';

type SettlementAccountChannelFieldsProps = {
  fields: SettlementChannelField[];
  info: BusinessPaymentMethodInfo | undefined;
  /** From the validator: which field it rejected, and why. */
  error?: SettlementAccountFieldError | null;
  onChange: (partial: Record<string, string>) => void;
};

// `Select` takes its label as a string, `Input` as a node, so the marker can
// only be hidden from assistive tech on the Input. `required` is what actually
// announces the field as required in both cases.
const requiredLabel = (field: SettlementChannelField): string =>
  field.required ? `${field.label} *` : field.label;

const requiredLabelNode = (field: SettlementChannelField): React.ReactNode =>
  field.required ? (
    <>
      {field.label} <span aria-hidden="true">*</span>
    </>
  ) : (
    field.label
  );

/**
 * Draws one channel's fields from the registry. Nothing here branches on the
 * channel: a new channel is a registry entry, and a field that needs a picker
 * is a field with `options`.
 *
 * Each control carries an `id` (which is also what associates its label) and,
 * when the validator rejected it, `aria-invalid` plus `aria-describedby`
 * pointing at the message rendered directly beneath it.
 */
const SettlementAccountChannelFields = ({
  fields,
  info,
  error,
  onChange,
}: SettlementAccountChannelFieldsProps) => (
  <>
    {fields.map((field) => {
      const value = readSettlementAccountField(info, field.name);
      const update = (next: string) => onChange({[field.name]: next});
      const fieldId = `settlement-account-${field.name}`;
      const message = error?.field === field.name ? error.message : undefined;
      const aria = {
        id: fieldId,
        required: field.required,
        'aria-invalid': message ? true : undefined,
        'aria-describedby': message ? `${fieldId}-error` : undefined,
      };

      return (
        <React.Fragment key={field.name}>
          {field.options?.length ? (
            <Select
              {...aria}
              label={requiredLabel(field)}
              dataCy={`merchant-settlement-accounts-form-${field.name}`}
              value={value}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                update(e.target.value)
              }
              options={field.options}
              className="w-full"
            />
          ) : (
            <Input
              {...aria}
              label={requiredLabelNode(field)}
              placeholder={field.placeholder}
              type={field.type ?? 'text'}
              dataCy={`merchant-settlement-accounts-form-${field.name}`}
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                update(e.target.value)
              }
              className="w-full isFull"
            />
          )}

          {message && (
            <p
              id={`${fieldId}-error`}
              className="mt-2 text-sm text-red-400"
              data-cy={`merchant-settlement-accounts-form-${field.name}-error`}
            >
              {message}
            </p>
          )}
        </React.Fragment>
      );
    })}
  </>
);

export default SettlementAccountChannelFields;
